import { useCallback, useEffect, useMemo, useState } from 'react';
import { ref, onValue, set, update, get } from 'firebase/database';
import { database } from '../firebase';
import { toast } from '../utils/toast';
import { toQuestionList, toQuestionMap } from '../data/surveyQuestions';
import { sha256Hex } from '../utils/adminAuth';

// Survey data lives under archives/_surveys, NOT a new top-level node: the
// interim deployed ruleset only opens `sessions/` and `archives/`, so a new
// root path would be denied until the locked rules ship. Same trick the case
// library uses (see CLAUDE.md, “Firebase data model”).
export const SURVEY_ROOT = 'archives/_surveys';
const CONFIG_PATH = `${SURVEY_ROOT}/config/questions`;
const RESPONSES_PATH = `${SURVEY_ROOT}/responses`;
const ADMIN_PATH = `${SURVEY_ROOT}/admin`;

// One response per person per case RUN, a session that runs three variants
// collects three sets, keyed by the run's reveal timestamp.
export const runIdOf = (caseFlow) => String(caseFlow?.diagnosisRevealedAt || '');
const skipKey = (sessionId, runId) => `cdmgrid:survey:${sessionId}:${runId}`;

/**
 * The student side: the live question set, plus whether this person has
 * already answered (or skipped) the current case run.
 */
export default function useSurvey(sessionId, identityKey, runId, active) {
  const [questionNode, setQuestionNode] = useState(null);
  const [answeredAt, setAnsweredAt] = useState(null);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    const unsub = onValue(ref(database, CONFIG_PATH), (snap) => setQuestionNode(snap.val()), () => {});
    return unsub;
  }, [active]);

  // Has this person already responded to this run? (Also covers a refresh on
  // another device, where the local skip flag wouldn't exist.)
  useEffect(() => {
    if (!active || !sessionId || !runId || !identityKey) return undefined;
    setSkipped(localStorage.getItem(skipKey(sessionId, runId)) === '1');
    const path = `${RESPONSES_PATH}/${sessionId}/${runId}/${identityKey}/at`;
    const unsub = onValue(ref(database, path), (snap) => setAnsweredAt(snap.val()), () => setAnsweredAt(null));
    return () => {
      unsub();
      setAnsweredAt(null);
    };
  }, [active, sessionId, runId, identityKey]);

  const questions = useMemo(() => toQuestionList(questionNode), [questionNode]);

  const submit = useCallback(
    (answers, meta = {}) => {
      if (!sessionId || !runId || !identityKey) return Promise.resolve();
      return set(ref(database, `${RESPONSES_PATH}/${sessionId}/${runId}/${identityKey}`), {
        at: Date.now(),
        sessionId,
        answers,
        ...meta,
      })
        .then(() => toast('Thanks, your feedback was recorded.', 'success'))
        .catch((error) => {
          console.error('Survey submit failed:', error);
          toast("Couldn't save your feedback, check your connection.");
        });
    },
    [sessionId, runId, identityKey]
  );

  const skip = useCallback(() => {
    if (sessionId && runId) localStorage.setItem(skipKey(sessionId, runId), '1');
    setSkipped(true);
  }, [sessionId, runId]);

  return { questions, submitted: !!answeredAt, skipped, submit, skip };
}

/**
 * The admin side: every response ever collected, plus editing the question set
 * and the dashboard password. Only subscribes once the dashboard is unlocked.
 */
export function useSurveyAdmin(active) {
  const [questionNode, setQuestionNode] = useState(null);
  const [responseNode, setResponseNode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active) return undefined;
    setLoading(true);
    const unsubQ = onValue(ref(database, CONFIG_PATH), (snap) => setQuestionNode(snap.val()), () => {});
    const unsubR = onValue(
      ref(database, RESPONSES_PATH),
      (snap) => { setResponseNode(snap.val()); setLoading(false); },
      (error) => { console.error('Survey read failed:', error); setLoading(false); }
    );
    return () => { unsubQ(); unsubR(); };
  }, [active]);

  const questions = useMemo(() => toQuestionList(questionNode), [questionNode]);

  // Flatten sessions → runs → people into one list, newest first.
  const responses = useMemo(() => {
    const out = [];
    Object.entries(responseNode || {}).forEach(([sessionId, runs]) => {
      Object.entries(runs || {}).forEach(([runId, people]) => {
        Object.entries(people || {}).forEach(([key, r]) => {
          out.push({
            key: `${sessionId}/${runId}/${key}`,
            sessionId,
            runId,
            identityKey: key,
            at: r?.at || 0,
            name: r?.name || 'Anonymous',
            role: r?.role || 'student',
            caseTitle: r?.caseTitle || ', ',
            caseId: r?.caseId || '',
            streamLabel: r?.streamLabel || '',
            answers: r?.answers || {},
          });
        });
      });
    });
    return out.sort((a, b) => b.at - a.at);
  }, [responseNode]);

  const saveQuestions = useCallback(
    (list) => set(ref(database, CONFIG_PATH), toQuestionMap(list))
      .then(() => toast('Survey questions saved, they apply to every future case.', 'success'))
      .catch((error) => {
        console.error('Question save failed:', error);
        toast("Couldn't save the questions, check your connection.");
      }),
    []
  );

  const setPassword = useCallback(
    async (next) => {
      const passHash = await sha256Hex(next);
      return update(ref(database, ADMIN_PATH), { passHash, updatedAt: Date.now() })
        .then(() => toast('Dashboard password changed.', 'success'))
        .catch((error) => {
          console.error('Password change failed:', error);
          toast("Couldn't change the password, check your connection.");
        });
    },
    []
  );

  const deleteResponse = useCallback(
    (row) => set(ref(database, `${RESPONSES_PATH}/${row.sessionId}/${row.runId}/${row.identityKey}`), null)
      .catch(() => toast("Couldn't delete that response.")),
    []
  );

  return { questions, responses, loading, saveQuestions, setPassword, deleteResponse };
}

// Read once, at unlock time, the stored hash overrides the built-in default.
export const fetchAdminHash = () =>
  get(ref(database, `${ADMIN_PATH}/passHash`))
    .then((snap) => snap.val() || null)
    .catch(() => null);
